/**
 * extractor.ts — Claude Code JSONL transcript -> PortableSession.
 *
 * Ported from the validated prototype (`cc-to-portable.mjs`). The prototype
 * STREAMS a file with readline because real on-disk sessions run 15-71 MB; here
 * we already hold the transcript in memory (fork.ts hands us `jsonlContent`), so
 * this parses an in-memory STRING instead (split on newlines). All extraction
 * logic is preserved: DAG ordering (incl. the tool_result-after-tool_call fix),
 * the `toolUseResult` sidecar, the fidelity rules, and thinking-signature strip.
 *
 * The one behavioral trim vs. the prototype: subagent fork transcripts live in
 * sibling files on the pod's disk we never receive, so `buildSubagents` derives
 * conclusions from the in-band `<task-notification>` records + the Agent/Task
 * sidecar only (no fork-output file read).
 */

import { execFileSync } from "node:child_process";

// ---------------------------------------------------------------------------
// PortableSession type (canonical intermediate; see DESIGN.md § Cross-Agent Resume)
// ---------------------------------------------------------------------------

export interface PortableSessionMeta {
  source_agent: string;
  source_agent_version?: string;
  source_session_id: string;
  cwd: string;
  git_branch?: string | null;
  git_head?: string | null;
  git_dirty?: boolean | null;
  started_at?: string | null;
  ended_at?: string | null;
  task_goal: string;
  permission_mode?: string | null;
  compacted?: { occurred: boolean; count: number };
}

export interface TimelineEntry {
  seq: number;
  uuid?: string | null;
  parent_uuid?: string | null;
  role: "user" | "assistant" | "tool";
  kind: "text" | "tool_call" | "tool_result" | "thinking";
  ts?: string | null;
  text?: string;
  tool?: { id?: string; name: string; input?: any };
  result?: {
    tool_use_id?: string;
    content?: any;
    structured?: any;
    is_error?: boolean;
    stale?: boolean;
    already_applied?: boolean;
    truncated?: boolean;
  };
  refs?: (string | number)[];
}

export interface TodoItem {
  content: string;
  status: "pending" | "in_progress" | "completed";
}

export interface Subagent {
  agent_id?: string;
  task?: string;
  launched_via?: string;
  model?: string | null;
  tool_calls_count?: number;
  status?: string;
  output_file?: string | null;
  conclusion?: string;
}

export interface FileTouched {
  path: string;
  ops: Array<"read" | "edit" | "write">;
  why?: string;
}

export interface Decision {
  what: string;
  why?: string;
  rejected_alternatives?: string[];
}

export interface CurrentState {
  last_action: string;
  blocker: string | null;
  next_step_hint: string;
}

export interface DroppedAudit {
  images: number;
  hook_attachments: number;
  thinking_signatures: number;
  compacted_turns: number;
  orphans: number;
  parse_errors: number;
}

export interface PortableSession {
  _schema: "portable-session";
  _version: 1;
  meta: PortableSessionMeta;
  timeline: TimelineEntry[];
  todo: TodoItem[];
  subagents: Subagent[];
  files_touched: FileTouched[];
  decisions: Decision[];
  current_state: CurrentState;
  open_questions: string[];
  dropped: DroppedAudit;
}

// ---------------------------------------------------------------------------
// small helpers
// ---------------------------------------------------------------------------

const tsMs = (r: any): number => {
  const t = Date.parse(r && r.timestamp);
  return Number.isNaN(t) ? 0 : t;
};

// cap a string, returning {text, truncated}. Used where a blob would bloat the
// PortableSession but the reasoning value is in the head (Read bodies, etc).
function cap(str: any, n: number): { text: string; truncated: boolean } {
  const s = typeof str === "string" ? str : String(str ?? "");
  if (s.length <= n) return { text: s, truncated: false };
  return { text: s.slice(0, n) + `\n…[+${s.length - n} chars elided]`, truncated: true };
}

const firstSentence = (s: any, n = 220): string =>
  cap((s || "").replace(/\s+/g, " ").trim().split(/(?<=[.!?])\s/)[0] || "", n).text;

// map.get(key) or create + set it; type-safe replacement for the prototype's
// `(m.get(k) || m.set(k, v).get(k))` idiom (which trips strictNullChecks).
function getOrSet<K, V>(map: Map<K, V>, key: K, make: () => V): V {
  let v = map.get(key);
  if (v === undefined) {
    v = make();
    map.set(key, v);
  }
  return v;
}

// tool -> fidelity class (see prototype §3 table)
const STALE_TOOLS = new Set(["Read", "Grep", "Glob", "NotebookRead", "WebFetch", "WebSearch", "LS"]);
const APPLIED_TOOLS = new Set(["Edit", "Write", "MultiEdit", "NotebookEdit"]);
const READ_OPS = new Set(["Read", "Grep", "Glob", "NotebookRead", "LS"]);
const SUBAGENT_TOOLS = new Set(["Agent", "Task"]);

interface Notification {
  agentId: string | null;
  outputFile: string | null;
  status: string | null;
  summary: string | null;
  result: string | null;
}

interface IngestResult {
  nodes: any[];
  nodeMap: Map<string, any>;
  meta: {
    version: string | null;
    cwd: string | null;
    gitBranch: string | null;
    permissionMode: string | null;
    aiTitle: string | null;
    lastPrompt: string | null;
    sessionId: string | null;
  };
  notifications: Map<string, Notification>;
  dropped: DroppedAudit;
  startedAt: string | null;
  endedAt: string | null;
  compactedCount: number;
}

// ---------------------------------------------------------------------------
// pass 1 — parse the transcript string, partition records, collect metadata
// ---------------------------------------------------------------------------

function ingest(content: string): IngestResult {
  const nodes: any[] = []; // uuid-bearing records, in file order
  const nodeMap = new Map<string, any>(); // uuid -> record
  const meta = {
    version: null as string | null,
    cwd: null as string | null,
    gitBranch: null as string | null,
    permissionMode: null as string | null,
    aiTitle: null as string | null,
    lastPrompt: null as string | null,
    sessionId: null as string | null,
  };
  const notifications = new Map<string, Notification>(); // tool_use_id -> notification
  const dropped: DroppedAudit = {
    images: 0,
    hook_attachments: 0,
    thinking_signatures: 0,
    compacted_turns: 0,
    orphans: 0,
    parse_errors: 0,
  };
  let minTs = Infinity;
  let maxTs = -Infinity;
  let fileIdx = 0;

  // The prototype streams via readline (crlfDelay); here we split the in-memory
  // string. `.trim()` on each line absorbs any trailing \r on CRLF transcripts.
  for (const line of content.split("\n")) {
    const raw = line.trim();
    if (!raw) continue;
    let rec: any;
    try {
      rec = JSON.parse(raw);
    } catch {
      dropped.parse_errors++;
      continue;
    }
    rec._i = fileIdx++;

    // Strip inline base64 image data at ingest so image-heavy sessions stay bounded.
    stripImages(rec, dropped);

    // envelope fields — first non-null wins (they repeat on every record)
    if (meta.version == null && rec.version) meta.version = rec.version;
    if (meta.cwd == null && rec.cwd) meta.cwd = rec.cwd;
    if (meta.gitBranch == null && rec.gitBranch) meta.gitBranch = rec.gitBranch;
    if (meta.sessionId == null && rec.sessionId) meta.sessionId = rec.sessionId;
    if (rec.permissionMode) meta.permissionMode = rec.permissionMode;

    switch (rec.type) {
      // ---- metadata records (no uuid, NOT in the DAG) ----------------------
      case "ai-title":
        meta.aiTitle = rec.aiTitle ?? meta.aiTitle;
        continue;
      case "last-prompt":
        meta.lastPrompt = rec.lastPrompt ?? meta.lastPrompt;
        continue;
      case "permission-mode":
        if (rec.permissionMode) meta.permissionMode = rec.permissionMode;
        continue;
      case "agent-name":
        continue;
      case "queue-operation":
        recordNotification(rec, notifications);
        continue;
      case "summary":
        continue; // classic CC compaction summary record (not on this build)
    }

    if (rec.isCompactSummary === true) dropped.compacted_turns++;

    if (rec.type === "attachment") {
      dropped.hook_attachments++; /* still a DAG node below */
    }

    if (rec.uuid) {
      nodes.push(rec);
      nodeMap.set(rec.uuid, rec);
      const t = tsMs(rec);
      if (t) {
        if (t < minTs) minTs = t;
        if (t > maxTs) maxTs = t;
      }
    }
  }

  return {
    nodes,
    nodeMap,
    meta,
    notifications,
    dropped,
    startedAt: minTs === Infinity ? null : new Date(minTs).toISOString(),
    endedAt: maxTs === -Infinity ? null : new Date(maxTs).toISOString(),
    compactedCount: dropped.compacted_turns,
  };
}

// Replace huge base64 image payloads with a marker in-place; count them.
function stripImages(rec: any, dropped: DroppedAudit): void {
  const content = rec.message && rec.message.content;
  if (!Array.isArray(content)) return;
  for (const b of content) {
    if (b && b.type === "image" && b.source && typeof b.source === "object" && b.source.data) {
      dropped.images++;
      b.source = { type: b.source.type, media_type: b.source.media_type, data: "<stripped base64>" };
    }
  }
}

// Parse a <task-notification> queue-operation into a per-tool-use record.
function recordNotification(rec: any, notifications: Map<string, Notification>): void {
  const c = typeof rec.content === "string" ? rec.content : "";
  if (!c.includes("<task-notification>")) return;
  const pick = (tag: string): string | null => {
    const m = c.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
    return m ? m[1].trim() : null;
  };
  const toolUseId = pick("tool-use-id");
  if (!toolUseId) return;
  notifications.set(toolUseId, {
    agentId: pick("task-id"),
    outputFile: pick("output-file"),
    status: pick("status"),
    summary: pick("summary"),
    result: pick("result"),
  });
}

// ---------------------------------------------------------------------------
// DAG ordering — order by parent->child + timestamp, forest for orphans
// ---------------------------------------------------------------------------

function orderNodes(nodes: any[], nodeMap: Map<string, any>, dropped: DroppedAudit): any[] {
  const children = new Map<string, string[]>();
  const roots: any[] = [];
  for (const rec of nodes) {
    const p = rec.parentUuid;
    if (p == null || !nodeMap.has(p)) {
      if (p != null) dropped.orphans++; // dangling parent -> extra root (resume-across-files / corrupt line)
      roots.push(rec);
    } else {
      getOrSet(children, p, () => []).push(rec.uuid);
    }
  }
  const byTs = (a: any, b: any): number => tsMs(a) - tsMs(b) || a._i - b._i;
  roots.sort(byTs);

  const ordered: any[] = [];
  const visited = new Set<string>();
  const stack = [...roots].reverse(); // preorder: first root popped first
  while (stack.length) {
    const rec = stack.pop();
    if (visited.has(rec.uuid)) continue;
    visited.add(rec.uuid);
    ordered.push(rec);
    const kids = (children.get(rec.uuid) || [])
      .map((u) => nodeMap.get(u))
      .filter(Boolean)
      .sort(byTs);
    for (let i = kids.length - 1; i >= 0; i--) stack.push(kids[i]);
  }
  // safety net: never silently drop a subtree (cycles / weird graphs)
  for (const rec of nodes) if (!visited.has(rec.uuid)) ordered.push(rec);
  return ordered;
}

// ---------------------------------------------------------------------------
// content normalization + structured-sidecar pruning
// ---------------------------------------------------------------------------

function normalizeResultContent(content: any, dropped: DroppedAudit): any {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return content == null ? "" : String(content);
  return content
    .map((b: any) => {
      if (!b || typeof b !== "object") return String(b ?? "");
      if (b.type === "text") return b.text || "";
      if (b.type === "image") {
        dropped.images++;
        return "[Image]";
      }
      if (b.type === "tool_reference") return `[tool_reference ${b.name || ""}]`.trim();
      return JSON.stringify(b);
    })
    .join("\n");
}

// Keep verbatim what the target must trust (commands, errors, patches); drop the
// bulky blobs it should re-read live instead (full file bodies, originalFile).
function pruneStructured(name: string, tur: any, resultFlags: { truncated?: boolean }): any {
  if (tur == null) return undefined;
  if (typeof tur !== "object") {
    // polymorphism: plain-string result
    const c = cap(tur, 8000);
    if (c.truncated) resultFlags.truncated = true;
    return c.text;
  }
  if (APPLIED_TOOLS.has(name)) {
    return dropUndef({
      type: tur.type,
      filePath: tur.filePath,
      structuredPatch: tur.structuredPatch,
      userModified: tur.userModified,
    });
  }
  if (name === "Read" && tur.file && typeof tur.file === "object") {
    const body = cap(tur.file.content ?? "", 2000);
    if (body.truncated) resultFlags.truncated = true;
    return {
      type: tur.type,
      file: dropUndef({
        filePath: tur.file.filePath,
        numLines: tur.file.numLines,
        startLine: tur.file.startLine,
        totalLines: tur.file.totalLines,
        content: body.text,
      }),
    };
  }
  if (SUBAGENT_TOOLS.has(name)) {
    // handled via subagents[]; keep the launch facts only
    return dropUndef({
      agentId: tur.agentId,
      status: tur.status,
      isAsync: tur.isAsync,
      resolvedModel: tur.resolvedModel,
      outputFile: tur.outputFile,
    });
  }
  return tur; // Bash/MCP/etc: stdout/stderr/exitCode are load-bearing — verbatim
}

const dropUndef = (o: any): any => {
  for (const k of Object.keys(o)) if (o[k] === undefined) delete o[k];
  return o;
};

// ---------------------------------------------------------------------------
// pass 2 — emit the timeline + gather everything derivable from it
// ---------------------------------------------------------------------------

interface BuildResult {
  timeline: any[];
  callMeta: Map<string, { name: string; input: any; seqIndex: number }>;
  agentResults: Map<string, any>;
  askQuestions: string[];
  filesTouched: Map<string, Set<string>>;
  subagentCalls: Array<{ id: string; name: string; input: any }>;
  latestTodo: TodoItem[] | null;
}

function buildTimeline(ordered: any[], dropped: DroppedAudit): BuildResult {
  const timeline: any[] = [];
  const callMeta = new Map<string, { name: string; input: any; seqIndex: number }>(); // tool_use_id -> meta
  const resultIds = new Set<string>(); // tool_use_ids that got a result
  const agentResults = new Map<string, any>(); // tool_use_id -> pruned/raw sidecar for Agent/Task
  const askQuestions: string[] = [];
  const filesTouched = new Map<string, Set<string>>(); // path -> Set(ops)
  const subagentCalls: Array<{ id: string; name: string; input: any }> = []; // {id, name, input}
  let latestTodo: TodoItem[] | null = null; // last non-empty TodoWrite snapshot we can reconstruct
  let seq = 0;

  const push = (e: any): any => {
    e.seq = seq++;
    timeline.push(e);
    return e;
  };

  for (const rec of ordered) {
    if (rec.type !== "user" && rec.type !== "assistant") continue;
    const base = { uuid: rec.uuid ?? null, parent_uuid: rec.parentUuid ?? null, ts: rec.timestamp ?? null };
    const content = rec.message && rec.message.content;

    // ---- user record ------------------------------------------------------
    if (rec.type === "user") {
      if (typeof content === "string") {
        push({ ...base, role: "user", kind: "text", text: content });
        continue;
      }
      if (!Array.isArray(content)) continue;
      // one sidecar (toolUseResult) per user record, 1:1 with the tool_result block
      const sidecar = rec.toolUseResult;
      const imgs = content.filter((b: any) => b && b.type === "image").length;
      const texts = content
        .filter((b: any) => b && b.type === "text")
        .map((b: any) => b.text)
        .filter(Boolean);
      if (texts.length || imgs) {
        const t = texts.join("\n") + (imgs ? ` ${"[Image]".repeat(1)} (${imgs} image${imgs > 1 ? "s" : ""})` : "");
        if (t.trim()) push({ ...base, role: "user", kind: "text", text: t.trim() });
      }
      for (const b of content) {
        if (!b || b.type !== "tool_result") continue;
        resultIds.add(b.tool_use_id);
        const meta = callMeta.get(b.tool_use_id);
        const name = meta ? meta.name : "";
        const flags: { truncated?: boolean } = {};
        const result: any = {
          tool_use_id: b.tool_use_id,
          content: cap(normalizeResultContent(b.content, dropped), 40000).text,
          is_error: b.is_error === true,
        };
        const structured = pruneStructured(name, sidecar, flags);
        if (structured !== undefined) result.structured = structured;
        if (STALE_TOOLS.has(name)) result.stale = true;
        if (APPLIED_TOOLS.has(name)) result.already_applied = true;
        if (flags.truncated) result.truncated = true;
        if (SUBAGENT_TOOLS.has(name) && sidecar && typeof sidecar === "object" && sidecar.agentId) {
          agentResults.set(b.tool_use_id, sidecar);
        }
        push({
          ...base,
          role: "tool",
          kind: "tool_result",
          result,
          refs: meta && meta.input && meta.input.file_path ? [meta.input.file_path] : [],
        });
      }
      continue;
    }

    // ---- assistant record -------------------------------------------------
    if (!Array.isArray(content)) continue;
    for (const b of content) {
      if (!b || typeof b !== "object") continue;
      if (b.type === "text") {
        push({ ...base, role: "assistant", kind: "text", text: b.text || "" });
      } else if (b.type === "thinking") {
        if (b.signature) dropped.thinking_signatures++; // drop signature, keep text
        push({ ...base, role: "assistant", kind: "thinking", text: b.thinking || "" });
      } else if (b.type === "tool_use") {
        const entry = push({
          ...base,
          role: "assistant",
          kind: "tool_call",
          tool: { id: b.id, name: b.name, input: b.input },
          refs: refsForTool(b),
        });
        callMeta.set(b.id, { name: b.name, input: b.input || {}, seqIndex: entry.seq });
        collectFromToolCall(b, {
          filesTouched,
          subagentCalls,
          askQuestions,
          setTodo: (v: TodoItem[]) => (latestTodo = v),
        });
      }
    }
  }

  // interrupted tool loop — a tool_use with no matching result. Synthesize one
  // right after the call so it surfaces as the last action / blocker.
  for (const [id] of callMeta) {
    if (resultIds.has(id)) continue;
    const at = timeline.findIndex((e) => e.kind === "tool_call" && e.tool && e.tool.id === id);
    const synth = {
      seq: -1,
      uuid: null,
      parent_uuid: null,
      role: "tool",
      kind: "tool_result",
      result: { tool_use_id: id, content: "<interrupted — no result captured>", is_error: true },
    };
    if (at >= 0) timeline.splice(at + 1, 0, synth);
    else timeline.push(synth);
  }
  const finalTimeline = pinResultsToCalls(timeline);
  finalTimeline.forEach((e, i) => (e.seq = i)); // renumber after splices + result pinning

  return { timeline: finalTimeline, callMeta, agentResults, askQuestions, filesTouched, subagentCalls, latestTodo };
}

// Put every tool_result immediately after its matching tool_call. The DAG walk
// sorts a node's children by ts, so a tool_use record with a non-result child
// that has an EARLIER ts emits that child's whole subtree between the call and
// its result. Re-slot each result right behind its call; orphan results keep
// their original position.
function pinResultsToCalls(timeline: any[]): any[] {
  const resultById = new Map<string, any>();
  for (const e of timeline) {
    if (
      e.kind === "tool_result" &&
      e.result &&
      e.result.tool_use_id != null &&
      !resultById.has(e.result.tool_use_id)
    ) {
      resultById.set(e.result.tool_use_id, e);
    }
  }
  const placed = new Set<any>();
  const out: any[] = [];
  for (const e of timeline) {
    if (e.kind === "tool_result" && placed.has(e)) continue; // already slotted behind its call
    out.push(e);
    if (e.kind === "tool_call" && e.tool && e.tool.id != null) {
      const res = resultById.get(e.tool.id);
      if (res && !placed.has(res)) {
        out.push(res);
        placed.add(res);
      }
    }
  }
  return out;
}

function refsForTool(b: any): Array<string | number> {
  const fp = b.input && (b.input.file_path || b.input.path || b.input.notebook_path);
  const refs: Array<string | number> = [];
  if (fp) refs.push(fp);
  if (SUBAGENT_TOOLS.has(b.name)) refs.push(b.id);
  return refs;
}

interface ToolCallSinks {
  filesTouched: Map<string, Set<string>>;
  subagentCalls: Array<{ id: string; name: string; input: any }>;
  askQuestions: string[];
  setTodo: (v: TodoItem[]) => void;
}

function collectFromToolCall(b: any, sinks: ToolCallSinks): void {
  const inp = b.input || {};
  const fp = inp.file_path || inp.notebook_path || (READ_OPS.has(b.name) ? inp.path : undefined);
  if (fp) {
    const op = APPLIED_TOOLS.has(b.name) ? (b.name === "Write" ? "write" : "edit") : "read";
    getOrSet(sinks.filesTouched, fp, () => new Set<string>()).add(op);
  }
  if (SUBAGENT_TOOLS.has(b.name)) sinks.subagentCalls.push({ id: b.id, name: b.name, input: inp });
  if (b.name === "AskUserQuestion" && Array.isArray(inp.questions)) {
    for (const q of inp.questions) if (q && q.question) sinks.askQuestions.push(q.question);
  }
  if (b.name === "TodoWrite" && Array.isArray(inp.todos)) {
    sinks.setTodo(inp.todos.map((t: any) => ({ content: t.content, status: t.status })));
  }
}

// ---------------------------------------------------------------------------
// subagents — correlate Agent tool_use with its async result + notification.
// The prototype also tailed the fork's output file; that file is not part of
// the transcript we receive, so conclusions come from the notification/sidecar.
// ---------------------------------------------------------------------------

function buildSubagents(
  subagentCalls: Array<{ id: string; name: string; input: any }>,
  agentResults: Map<string, any>,
  notifications: Map<string, Notification>
): Subagent[] {
  const out: Subagent[] = [];
  for (const { id, name, input } of subagentCalls) {
    const sc = agentResults.get(id) || {};
    const notif = notifications.get(id) || ({} as Partial<Notification>);
    const agentId = sc.agentId || notif.agentId || null;
    const outputFile = sc.outputFile || notif.outputFile || null;

    const conclusion = notif.result || notif.summary || "";
    out.push(
      dropUndef({
        agent_id: agentId || undefined,
        task: input.description || firstSentence(input.prompt || "", 200) || undefined,
        launched_via: name,
        model: sc.resolvedModel || null,
        status: notif.status || sc.status || undefined,
        output_file: outputFile || null,
        conclusion,
      })
    );
  }
  return out;
}

// ---------------------------------------------------------------------------
// analytic fields — best-effort, grounded in real text; never fabricate
// ---------------------------------------------------------------------------

// open_questions are grounded in explicit AskUserQuestion calls only. decisions[]
// is left empty on real data — an honest blank beats a heuristic guess.
function groundedOpenQuestions(askQuestions: string[]): string[] {
  return dedupBy(askQuestions.filter(Boolean), (s) => s).slice(0, 8);
}

function deriveCurrentState(timeline: any[], todo: TodoItem[] | null): CurrentState {
  let lastAction = "";
  let blocker: string | null = null;
  let nextHint = "";
  for (let i = timeline.length - 1; i >= 0; i--) {
    const e = timeline[i];
    if (!lastAction && e.role === "assistant" && e.kind === "tool_call") lastAction = describeCall(e);
    if (!lastAction && e.role === "assistant" && e.kind === "text" && e.text.trim())
      lastAction = cap(e.text, 240).text.trim();
    if (!blocker && e.kind === "tool_result" && e.result && e.result.is_error)
      blocker = cap(String(e.result.content || ""), 400).text.trim();
    if (lastAction && blocker) break;
  }
  for (let i = timeline.length - 1; i >= 0; i--) {
    const e = timeline[i];
    if (e.role === "assistant" && e.kind === "text" && e.text.trim()) {
      nextHint = cap(e.text, 400).text.trim();
      break;
    }
  }
  if (!nextHint && Array.isArray(todo)) {
    const pending = todo.find((t) => t.status !== "completed");
    if (pending) nextHint = `Next open task: ${pending.content}`;
  }
  return { last_action: lastAction, blocker, next_step_hint: nextHint };
}

const describeCall = (e: any): string => {
  const inp = e.tool.input || {};
  const arg = inp.command || inp.file_path || inp.pattern || inp.description || inp.query || "";
  return cap(`${e.tool.name}: ${typeof arg === "string" ? arg : JSON.stringify(arg)}`, 240).text.trim();
};

const dedupBy = <T,>(arr: T[], key: (x: T) => string): T[] => {
  const seen = new Set<string>();
  return arr.filter((x) => {
    const k = key(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

// ---------------------------------------------------------------------------
// task_goal
// ---------------------------------------------------------------------------

function deriveTaskGoal(timeline: any[], meta: IngestResult["meta"]): string {
  const firstUser = timeline.find((e) => e.role === "user" && e.kind === "text");
  let goal = firstUser ? stripCommandWrapper(firstUser.text) : "";
  if (!goal) goal = meta.aiTitle || meta.lastPrompt || "";
  return goal || "(unknown — no user prompt found)";
}

function stripCommandWrapper(t: string): string {
  return (t || "")
    .replace(/<command-[^>]*>[\s\S]*?<\/command-[^>]*>/g, " ")
    .replace(/<local-command-[^>]*>[\s\S]*?<\/local-command-[^>]*>/g, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// ---------------------------------------------------------------------------
// worktree fingerprint — best-effort; never fail extraction
// ---------------------------------------------------------------------------

// Capture the session cwd's git HEAD + dirty state so the resume artifact can
// warn when the current HEAD has drifted (the [APPLIED]/[STALE] flags may be
// off). fork.ts patches the transcript's cwd to the local git root before
// calling us, so this fingerprints the LOCAL repo. Returns {head:null,dirty:null}
// when cwd is unknown or not a git repo.
function gitFingerprint(cwd: string | null): { head: string | null; dirty: boolean | null } {
  if (!cwd) return { head: null, dirty: null };
  const git = (args: string[]): string | null => {
    try {
      return execFileSync("git", ["-C", cwd, ...args], {
        encoding: "utf8",
        stdio: ["ignore", "pipe", "ignore"],
      }).trim();
    } catch {
      return null;
    }
  };
  const head = git(["rev-parse", "HEAD"]);
  if (head == null) return { head: null, dirty: null }; // not a git repo / no commits
  const status = git(["status", "--porcelain"]);
  return { head, dirty: status == null ? null : status.length > 0 };
}

// ---------------------------------------------------------------------------
// assemble
// ---------------------------------------------------------------------------

/**
 * Parse a Claude Code JSONL transcript (in memory) into a PortableSession.
 * Pure w.r.t. the transcript; the only side effect is a best-effort read-only
 * `git` fingerprint of the transcript's cwd (never throws).
 */
export function extractPortableSession(jsonlContent: string): PortableSession {
  const ing = ingest(jsonlContent);
  const ordered = orderNodes(ing.nodes, ing.nodeMap, ing.dropped);
  const b = buildTimeline(ordered, ing.dropped);

  const todo = b.latestTodo || [];
  const subagents = buildSubagents(b.subagentCalls, b.agentResults, ing.notifications);

  const filesTouched: FileTouched[] = [...b.filesTouched].map(([p, ops]) => ({
    path: p,
    ops: [...ops] as Array<"read" | "edit" | "write">,
  }));
  const git = gitFingerprint(ing.meta.cwd);

  const meta: PortableSessionMeta = dropUndef({
    // Present the source as "Sailfish Autofix", not the underlying coding agent.
    // This value surfaces in the customer-written RESUME.md (front-matter + prose)
    // and resume.json, so it must not advertise the engine (the transcript is
    // claude-code-format JSONL, but that's an implementation detail).
    source_agent: "sailfish-autofix",
    source_agent_version: ing.meta.version || undefined,
    source_session_id: ing.meta.sessionId || "", // from the transcript's sessionId field
    cwd: ing.meta.cwd || "(unknown)",
    git_branch: ing.meta.gitBranch ?? null,
    git_head: git.head,
    git_dirty: git.dirty,
    started_at: ing.startedAt,
    ended_at: ing.endedAt,
    task_goal: deriveTaskGoal(b.timeline, ing.meta),
    permission_mode: ing.meta.permissionMode ?? null,
    compacted: { occurred: ing.compactedCount > 0, count: ing.compactedCount },
  });

  return {
    _schema: "portable-session",
    _version: 1,
    meta,
    timeline: b.timeline,
    todo,
    subagents,
    files_touched: filesTouched,
    decisions: [],
    current_state: deriveCurrentState(b.timeline, todo),
    open_questions: groundedOpenQuestions(b.askQuestions),
    dropped: ing.dropped,
  };
}
